import { type SelectProps, type TextFieldProps } from "@mui/material"

import { INCH_UOM_ID, UOM_PAIRS } from "../constants"
import { type PackagingInfoFormValues } from "../PackagingInfo"
import type { PackagingInfo } from "../types/packaging"

import { SELECT_STYLING_PROPS, TEXT_FIELD_STYLING_PROPS } from "@/constants/forms"
import type { Product } from "@/graphql/codegen/graphql"
import { getProductPackagingTypeLabel } from "@/utils/product"

export const getNumberTextFieldProps = (
  fieldState: { error?: unknown },
  disabled: boolean,
  placeholder = "0.00"
): Partial<TextFieldProps> => ({
  ...TEXT_FIELD_STYLING_PROPS,
  disabled,
  error: !!fieldState.error,
  placeholder,
  type: "number",
})

export const getSelectFieldProps = (fieldState: any, disabled: boolean): Partial<SelectProps> =>
  ({
    ...SELECT_STYLING_PROPS,
    disabled,
    error: !!fieldState.error,
  }) as Partial<SelectProps>

export function serializeProductPackagingForm({ uoms, ...formValues }: PackagingInfoFormValues): PackagingInfo {
  const { us: usUom, metric: metricUom } = uoms
  const numericFields: (keyof Omit<PackagingInfoFormValues, "form">)[] = [
    "packageUsWeight",
    "packageMetricWeight",
    "packageLength",
    "packageWidth",
    "packageHeight",
    "palletUsWeight",
    "palletMetricWeight",
    "palletSizeLength",
    "palletSizeWidth",
    "palletSizeHeight",
    "packagesPerPallet",
  ] as const

  const serializedForm = Object.entries(formValues).reduce(
    (acc, [key, value]) => {
      if (value === undefined) {
        return acc
      }

      if (value === "") {
        acc[key] = null
        return acc
      }

      if (numericFields.includes(key as (typeof numericFields)[number])) {
        acc[key] = Number(value)
      } else {
        acc[key] = value
      }

      return acc
    },
    {} as Record<string, unknown>
  )

  return {
    ...serializedForm,
    packageUsWeightUom: usUom,
    packageMetricWeightUom: metricUom,
    palletUsWeightUom: usUom,
    palletMetricWeightUom: metricUom,
  }
}

export function transformToFormData(data: Partial<Product> | null): PackagingInfoFormValues | null {
  if (!data) return null

  return {
    ...data,
    freightItem: data.freightItem || null,
    freightClass: data.freightClass || null,
    packagingType: data.packagingType || "",
    packageUsWeight: String(data.packageUsWeight || ""),
    packageMetricWeight: String(data.packageMetricWeight || ""),
    packageLength: data.packageLength ? String(data.packageLength) : undefined,
    packageWidth: data.packageWidth ? String(data.packageWidth) : undefined,
    packageHeight: data.packageHeight ? String(data.packageHeight) : undefined,
    palletUsWeight: String(data.palletUsWeight || ""),
    palletMetricWeight: String(data.palletMetricWeight || ""),
    uoms: {
      us: data.packageUsWeightUom || "",
      metric: data.packageMetricWeightUom || "",
    },
    palletSizeLength: data.palletSizeLength ? String(data.palletSizeLength) : undefined,
    palletSizeWidth: data.palletSizeWidth ? String(data.palletSizeWidth) : undefined,
    palletSizeHeight: data.palletSizeHeight ? String(data.palletSizeHeight) : undefined,
    palletSizeUom: data.palletSizeUom || INCH_UOM_ID,
    packagesPerPallet: data.packagesPerPallet ? String(data.packagesPerPallet) : undefined,
  }
}

export function createDetailListRows(packagingInfo: PackagingInfo | null): {
  label: string
  values: any[]
}[] {
  if (!packagingInfo) {
    return []
  }

  const {
    freightItem,
    freightClass,
    packagingType,
    packageUsWeight,
    palletUsWeight,
    packageMetricWeight,
    palletMetricWeight,
    packageLength,
    packageWidth,
    packageHeight,
    palletSizeLength,
    palletSizeWidth,
    palletSizeHeight,
    packagesPerPallet,
    packageUsWeightUomDetails,
    packageMetricWeightUomDetails,
    palletMetricWeightUomDetails,
    palletUsWeightUomDetails,
  } = packagingInfo

  const packagingTypeLabel = getProductPackagingTypeLabel(packagingType)

  const listRows = [
    { label: "Freight Item / Class", values: [freightItem ?? "-", freightClass ?? "-"] },
    {
      label: "Std. Packaging",
      values: [packagingTypeLabel || "-"],
    },
    {
      label: "Std. UoM Quantity",
      values: [
        `${packageUsWeight ?? "-"} ${packageUsWeightUomDetails?.name ?? "-"}`,
        "/",
        `${packageMetricWeight ?? "-"} ${packageMetricWeightUomDetails?.name ?? "-"}`,
      ],
    },
    {
      label: `Dimensions (in)
        ${packagingTypeLabel ? " of the " + packagingTypeLabel : ""}`,
      values: [`L ${packageLength ?? "-"}`, `W ${packageWidth ?? "-"}`, `H ${packageHeight ?? "-"}`],
    },
    {
      label: "UoM Quantity per Pallet",
      values: [
        `${palletUsWeight ?? "-"} ${palletUsWeightUomDetails?.name ?? "-"}`,
        "/",
        `${palletMetricWeight ?? "-"} ${palletMetricWeightUomDetails?.name ?? "-"}`,
      ],
    },
    {
      label: "Quantity per Pallet",
      values: [packagesPerPallet?.toString() ?? "-"],
    },
    {
      label: "Dimensions (in)",
      values: [`L ${palletSizeLength ?? "-"}`, `W ${palletSizeWidth ?? "-"}`, `H ${palletSizeHeight ?? "-"}`],
    },
  ]

  return listRows
}

export function validatePackagingWeights(packagingInfo: PackagingInfo | null): boolean {
  if (!packagingInfo) {
    return false
  }

  const { palletMetricWeight, palletUsWeight, packageMetricWeight, packageUsWeight } = packagingInfo

  return [palletMetricWeight, palletUsWeight, packageMetricWeight, packageUsWeight].every(Boolean)
}

export function findMatchingUomPair(packagingInfo: PackagingInfo | null): (typeof UOM_PAIRS)[number] | null {
  if (!packagingInfo) {
    return null
  }

  const { packageUsWeightUom, packageMetricWeightUom, palletUsWeightUom, palletMetricWeightUom } = packagingInfo

  return (
    UOM_PAIRS.find(
      ({ value }) =>
        packageUsWeightUom === value.us &&
        palletUsWeightUom === value.us &&
        packageMetricWeightUom === value.metric &&
        palletMetricWeightUom === value.metric
    ) ?? null
  )
}
